import { useMsal } from '@azure/msal-react';
import { CommandBar, ICommandBarItemProps, IPanelProps, IRenderFunction, IStackProps, PivotItem, Separator, Stack, Text, keyframes, mergeStyles, useTheme } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { RouterParams } from 'App';
import { RootState } from 'app/store';
import MountConfetti from 'assets/confetti';
import NumberFieldControl, { ENumberFieldControlFormat } from 'components/controls/NumberFieldControl';
import TextFieldControl from 'components/controls/TextFieldControl';
import { EValidationSeverity } from 'components/controls/ValidationView/ValidationView.type';
import saveAs from 'file-saver';
import { CreateSnapshotFromOption, VisibilityDefault, VisibilityFromQuoteRequest, emitCustomEvent, isBaseAdminQuoteTypeCode } from 'global/Application';
import { AppDataSource } from 'global/DataSources/AppDataSource';
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import { changeProperty, cloneOpportunityProductOption } from 'global/Reducer/dataSlice';
import { setProcessing } from 'global/Reducer/statusSlice';
import * as IXrmOptions from 'model/AttributeOptionSets.type';
import {
    IOpportunityProduct as FAM_IOpportunityProduct,
    IOpportunityProductOptionBenefit as FAM_IOpportunityProductOptionBenefit
} from 'model/FlexibleAttributeModel';
import React, { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import FlexibleFieldView, { EScopeType } from 'views/BlueRelationship/FlexibleFieldView';
import ValidationItem from 'views/BlueRelationship/ValidationDialog/ValidationItem';

interface QuoteRequestItemProps {
    optionBenefit: FAM_IOpportunityProductOptionBenefit;
    opportunityProduct: FAM_IOpportunityProduct;
}

interface ISection {
    name: string;
    children: any;
}



type TIOpportunityProductOptionBenefitProperties = {
    label: string,
    field: keyof FAM_IOpportunityProductOptionBenefit,
    type: 'NumberFieldControl',
    format: ENumberFieldControlFormat,
    hide?: boolean,
    disabled?: boolean,
};



// TODO make this a general component
const Section: React.FunctionComponent<ISection> = (props) => {
    const theme = useTheme();
    const columnProps: Partial<IStackProps> = {
        tokens: { childrenGap: 15 },
        styles: {
            root: {
                width: 600,
                // width:'98%',
                // minWidth:'97%',
                // maxWidth:'99%',
                boxShadow: theme.effects.elevation4,
                padding: '10px 20px 20px 20px',
                margin: '20px 0px 0px 0px',
                backgroundColor: theme.semanticColors.menuBackground,
                borderRadius: '8px',
            },
        },
    };

    return <Stack.Item  {...columnProps}>
        <Stack.Item>
            <Stack
                horizontal
                horizontalAlign='space-between'
                verticalAlign='baseline'
            >
                <Text
                    variant='medium'
                    styles={{
                        root: {
                            color: '#0078D7',
                            textTransform: 'uppercase',
                            fontWeight: '600',
                            marginTop: '5px'
                        }
                    }}
                >
                    {props.name}
                </Text>

            </Stack>
        </Stack.Item>
        <Separator />
        <Stack.Item >
            <Stack tokens={{ childrenGap: '10px' }}>
                {props.children}
            </Stack>
        </Stack.Item>
    </Stack.Item>

}

const QuoteRequestItem: React.FC<QuoteRequestItemProps> = (props) => {

    const [isNavPinned, setIsNavPinned] = React.useState(true);
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const state = useSelector((state: RootState) => state);
    const dispatch = useDispatch();

    const theme = useTheme();
    const { bcbs_opportunityoptionid: routerParams_bcbs_opportunityoptionid }: RouterParams = useParams();

    const optionBenefit = props.optionBenefit;
    const quoteRequestCategory = state.data.config.cache.map_bcbs_config_category_named['Quote Request'];

    const confettiRef = useRef<any>(null);
    const confettiId = useId('confetti-');

    const { menuItems, farMenuItems } = useMemo(() => {
        const menuItems: ICommandBarItemProps[] = []

        menuItems.push({
            key: 'label',
            text: optionBenefit.name,
        });


        const farMenuItems: ICommandBarItemProps[] = [];

        farMenuItems.push({
            key: 'won',
            text: optionBenefit.won ? 'Won' : 'Flag as Won',
            iconProps: { iconName: 'Medal' },
            onRenderIcon: optionBenefit.won
                ? (() => <Text>🥇</Text>)
                : undefined,
            onClick(ev, item) {
                dispatch(
                    changeProperty({
                        type: 'bcbs_opportunityoption',
                        category: quoteRequestCategory,
                        scope: optionBenefit,
                        property: 'won',
                        value: { type: 'bool', value: !optionBenefit.won },
                    })
                );

                const confetti = MountConfetti(confettiId);
                confettiRef.current = confetti;
                setTimeout(() => {
                    confetti.stop();
                }, 8000);
            }
        });

        farMenuItems.push({
            key: 'select',
            text: 'Select',
            iconProps: {
                iconName: !optionBenefit.select
                    ? 'Checkbox'
                    : 'CheckboxComposite'
            },
            buttonStyles: {
                root: {
                    backgroundColor: !optionBenefit.select ? undefined : theme.semanticColors.warningBackground,
                }
            },
            onClick(ev, item) {
                dispatch(
                    changeProperty({
                        type: 'bcbs_opportunityoption',
                        category: quoteRequestCategory,
                        scope: optionBenefit,
                        property: 'select',
                        value: {
                            type: 'bool',
                            value: !optionBenefit.select
                        }
                    })
                );
            },
        });

        const moreSubMenu = [] as ICommandBarItemProps[];

        moreSubMenu.push({
            key: 'clone',
            text: 'Clone',
            iconProps: { iconName: 'Copy' },
            onClick(ev, item) {
                dispatch(cloneOpportunityProductOption(optionBenefit.id!));
            },
        });

        moreSubMenu.push({
            key: 'deactivate',
            text: optionBenefit.deactivated
                ? 'Activate '
                : 'Deactivate',
            title: optionBenefit.deactivated
                ? 'Activate '
                : 'Deactivate',
            iconProps: { iconName: 'DeactivateOrders' },
            onClick(ev, item) {

                dispatch(
                    changeProperty({
                        type: 'bcbs_opportunityoption',
                        category: quoteRequestCategory,
                        scope: optionBenefit,
                        property: 'deactivated',
                        value: { type: 'bool', value: !optionBenefit.deactivated }
                    })
                );
            }
        });

        moreSubMenu.push({
            key: 'Benefit Summary Report',
            text: 'Benefit Summary Report',
            title: 'Benefit Summary Report',
            iconProps: { iconName: 'ReportDocument' },
            disabled: true,
            onClick: (ev, item) => {
                (async () => {
                    try {
                        dispatch(setProcessing(true));

                        const accountId = state.data.opportunityProduct?.bcbs_opportunityproduct?._bcbs_groupid_value!;
                        const tempId = uuidv4();

                        const snapshot = CreateSnapshotFromOption(state, props.optionBenefit);

                        // lest save the a temporary snapshot for back end to generate the report
                        var saveResult = await dataSource.benefitSummaryReport_data_saveAsync(
                            accountId,
                            tempId,
                            snapshot
                        );

                        if (!saveResult?.blobName)
                            throw new Error('Failed to a temporary snapshot for back end to generate the report from.');

                        var response = await dataSource.getBenefitSummaryReport(
                            null,
                            undefined,
                            {
                                accountId: accountId,
                                blobName: saveResult?.blobName!,
                            }
                        )

                        saveAs(response.blob, response.fileName);

                    }
                    catch (error: any) {
                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });

                        throw error
                    }
                    finally {
                        dispatch(setProcessing(false));
                    }

                })();
            }
        });


        moreSubMenu.push({
            key: 'pin',
            text: isNavPinned ? 'Unpin' : 'Pin',
            iconProps: { iconName: isNavPinned ? 'PinnedSolid' : 'Pin' },
            onClick(ev, item) {
                setIsNavPinned(!isNavPinned);

                // setting sticky back to relieve causes a scroll, there is a bug in browser I think if the anchor point is lost
                // might 
                var scrollPost = { y: window.scrollY, x: window.scrollX };
                setImmediate(() => {
                    window.scrollTo(scrollPost.x, scrollPost.y)
                });
            }
        });

        farMenuItems.push({
            key: 'more',
            text: '',
            title: 'More',
            iconProps: { iconName: 'More' },
            subMenuProps: {
                items: moreSubMenu
            }
        });

        return { menuItems, farMenuItems };

    }, [optionBenefit, confettiId, isNavPinned, theme]);


    const disabled = optionBenefit.deactivated;

    const bcbs_opportunityproduct = props.opportunityProduct.bcbs_opportunityproduct;

    const onRenderUnderWriting: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        // If Quote Type of related Opportunity Product is not equal to Fully Insured or Level Funded, hide Underwriting fields:
        const is_bcbs_quotetypecode_lf_fi = bcbs_opportunityproduct.bcbs_quotetypecode === IXrmOptions.bcbs_opportunityproduct.bcbs_quotetypecode['Fully Insured(808900002)'].value
            || bcbs_opportunityproduct.bcbs_quotetypecode === IXrmOptions.bcbs_opportunityproduct.bcbs_quotetypecode['Level Funded(808900004)'].value


        const enrollmentFields: TIOpportunityProductOptionBenefitProperties[] = [
            {
                label: 'Single',
                field: 'bcbs_buyupuwsinglei',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.int,
                hide: !is_bcbs_quotetypecode_lf_fi,
            },
            {
                label: 'Family',
                field: 'bcbs_buyupuwfamilyi',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.int,
                hide: !is_bcbs_quotetypecode_lf_fi,
            },
            {
                label: 'EE/Children',
                field: 'bcbs_buyupuweechildreni',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.int,
                hide: !is_bcbs_quotetypecode_lf_fi,
            },
            {
                label: 'EE/Spouse',
                field: 'bcbs_buyupuweespousei',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.int,
                hide: !is_bcbs_quotetypecode_lf_fi,
            },
            {
                label: 'Enrollment Total',
                field: 'bcbs_buyupuwtotali',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.int,
                hide: false,
                disabled: !isBaseAdminQuoteTypeCode(bcbs_opportunityproduct.bcbs_quotetypecode),
            },
        ]

        const rateFields: TIOpportunityProductOptionBenefitProperties[] = [
            {
                label: 'Single',
                field: 'bcbs_buyupuwsinglem',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.currency,
                hide: !is_bcbs_quotetypecode_lf_fi,
            },
            {
                label: 'Family',
                field: 'bcbs_buyupuwfamilym',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.currency,
                hide: !is_bcbs_quotetypecode_lf_fi,
            },
            {
                label: 'EE / Children',
                field: 'bcbs_buyupuweechildrenm',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.currency,
                hide: !is_bcbs_quotetypecode_lf_fi,
            },
            {
                label: 'EE / Spouse',
                field: 'bcbs_buyupuweespousem',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.currency,
                hide: !is_bcbs_quotetypecode_lf_fi,
            },
            {
                label: isBaseAdminQuoteTypeCode(bcbs_opportunityproduct.bcbs_quotetypecode) ? 'Base Admin Fee' : 'Rates Total',
                field: 'bcbs_buyupuwtotalm',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.currency,
                hide: false
            },
            {
                label: 'Annual Premium',
                field: 'bcbs_annualpremium',
                type: 'NumberFieldControl',
                format: ENumberFieldControlFormat.currency,
                hide: !is_bcbs_quotetypecode_lf_fi,
                disabled: true,
            },
        ]

        return < PivotItem
            key={"underwriting"}
            headerText={"Underwriting"}
            style={{ marginBottom: '25px' }}
        >

            <Section name={'Enrollment'}>
                {enrollmentFields.filter(t => !t.hide).map(eachField => {
                    return (
                        <NumberFieldControl
                            disabled={eachField.disabled}
                            key={eachField.label}
                            label={eachField.label}
                            format={eachField.format}
                            fieldValueOnChange={(event, value) => {
                                dispatch(
                                    changeProperty({
                                        type: 'bcbs_opportunityoption',
                                        category: quoteRequestCategory,
                                        scope: optionBenefit,
                                        property: eachField.field,
                                        value: value
                                    })
                                );
                            }}
                            defaultFieldValue={{
                                type: 'number',
                                value: (optionBenefit as any)[eachField.field]!
                            }}
                        />
                    )
                })}
            </Section>

            <Section name={'Rates'}>
                {rateFields.filter(t => !t.hide).map(eachField =>
                    <NumberFieldControl
                        disabled={eachField.disabled}
                        key={eachField.label}
                        label={eachField.label}
                        format={eachField.format}
                        fieldValueOnChange={(event, value) => {
                            dispatch(
                                changeProperty({
                                    type: 'bcbs_opportunityoption',
                                    category: quoteRequestCategory,
                                    scope: optionBenefit,
                                    property: eachField.field,
                                    value: value
                                })
                            );
                        }}
                        defaultFieldValue={{
                            type: 'number',
                            value: (optionBenefit as any)[eachField.field]!
                        }}
                    />
                )}
            </Section>

        </PivotItem>
    }

    const onRenderProperties: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        return <PivotItem
            key={"properties"}
            headerText={"Properties"}
        >
            <Section name='Properties'>
                <TextFieldControl
                    label={"Name"}
                    fieldValueOnChange={(event, value) => {
                        dispatch(
                            changeProperty({
                                type: 'bcbs_opportunityoption',
                                category: quoteRequestCategory,
                                scope: optionBenefit,
                                property: 'name',
                                value: value
                            })
                        );
                    }}
                    defaultFieldValue={{
                        type: 'string',
                        value: optionBenefit.name
                    }}
                />

            </Section>

            <Separator />
        </PivotItem>
    }

    const flasherClassName = useMemo(() => {
        let flashBackgroundKeyframes = keyframes({
            '0%': {
                background: '#fff09e'
            },
            '20%': {
                background: 'none'
            },
            '40%': {
                background: '#fff09e'
            },
            '50%': {
                background: '#none'
            },
            '60%': {
                background: '#fff09e'
            },
            '100%': {
                background: 'none'
            }
        });

        const flasherClassName = mergeStyles({
            animation: `${flashBackgroundKeyframes} 3s ease`
        });

        return flasherClassName
    }, []);


    const wonStyle = useMemo(() => {
        let keyframesNames = keyframes({
            '0%': {
                opacity: '1'
            },
            '100%': {
                opacity: '0'
            }
        });

        const styleName = mergeStyles({
            animation: `${keyframesNames} 8s ease`
        });

        return styleName
    }, []);

    const visibility = useMemo(() => {
        const visibility = state.data.opportunityProduct?.bcbs_quoterequest && state.data.opportunityProduct?.bcbs_opportunityproduct
            ? VisibilityFromQuoteRequest(
                state.data.opportunityProduct?.bcbs_quoterequest,
                state.data.opportunityProduct?.bcbs_opportunityproduct,
            )
            : VisibilityDefault();

        return visibility;
    }, [state.data.opportunityProduct?.bcbs_quoterequest])


    const styleNav = useMemo(() => {

        let styleNav: React.CSSProperties = {
            // position: 'relative',
            top: '43px',
            zIndex: 1,
        }

        if (state.status.isNavPinned && isNavPinned)
            styleNav = {
                ...styleNav,
                position: 'sticky',
            }

        return styleNav;

    }, [state.status.isNavPinned, isNavPinned]);

    return (
        <Stack
            id={"anchor-" + optionBenefit.id}
            styles={{
                root: {
                    boxShadow: theme.effects.elevation4,
                    padding: '8px',
                    borderRadius: '8px',

                }
            }}
            className={optionBenefit.id === routerParams_bcbs_opportunityoptionid ? flasherClassName : undefined}
        >
            <div style={styleNav} >
                <CommandBar
                    styles={{
                        root: {
                            backgroundColor: theme.palette.white,
                            boxShadow: theme.effects.elevation4,
                            borderRadius: '8px',
                        }
                    }}
                    items={menuItems}
                    farItems={farMenuItems}
                />
                <canvas
                    id={confettiId}
                    key={confettiId}
                    className={optionBenefit.won ? wonStyle : undefined}
                    style={{
                        position: 'absolute',
                        top: '0px',
                        left: '0px',
                        height: '100%',
                        width: '100%',
                        pointerEvents: 'none',
                        display: optionBenefit.won ? 'block' : 'none'
                    }}
                />

            </div>
            {optionBenefit.validationMessages.length > 0 &&
                <Stack.Item style={{ width: '635px' }} >
                    <Separator />
                    <ValidationItem
                        data={{
                            title: 'Failed to display option fields due to validation errors.',
                            type: EValidationSeverity.error,
                            inner: optionBenefit.validationMessages,
                        }}
                    />
                </Stack.Item>
            }
            {onRenderProperties()}

            {optionBenefit.validationMessages.length === 0 &&
                <FlexibleFieldView
                    disabled={disabled}
                    fields={optionBenefit.fields!}
                    category={quoteRequestCategory}
                    tiers={optionBenefit.bcbs_tierdefinition.tierLevels}
                    cancellation={false}
                    scope={optionBenefit}
                    scopeType={EScopeType.opportunityProductOptionBenefitFieldSet}
                    visibility={visibility}
                    lastPivotItem={[onRenderUnderWriting]}
                />
            }

        </Stack>
    )
};

export default QuoteRequestItem;

